// components/SendAccountEmailModal.jsx

import React, { useState, useEffect } from "react";
import { X, Send, FileText } from "lucide-react";
import apiService from "../services/api";
import { useTheme } from "../contexts/ThemeContext";

const SendAccountEmailModal = ({ accountData, onClose, onSuccess }) => {
  const { isDarkMode } = useTheme();
  const [formData, setFormData] = useState({
    to: "",
    subject: "",
    body: "",
    bodyHtml: "",
    cc: "",
    bcc: "",
    trackOpens: true,
    trackLinks: true,
  });
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [loading, setLoading] = useState(false);
  const [emailStatus, setEmailStatus] = useState(null);

  useEffect(() => {
    loadTemplates();
    checkEmailConnection();

    // Set default recipient from account
    if (accountData) {
      let defaultTo = "";

      // Try to get email from primary contact
      if (accountData.contacts && accountData.contacts.length > 0) {
        const primaryContact = accountData.contacts[0];
        defaultTo = primaryContact.email || "";
      } else if (accountData.email) {
        // Fallback to account email
        defaultTo = accountData.email;
      }

      setFormData((prev) => ({
        ...prev,
        to: defaultTo,
        subject: `${accountData.name}`,
      }));
    }
  }, [accountData]);

  const checkEmailConnection = async () => {
    try {
      const response = await apiService.getEmailConnectionStatus();
      setEmailStatus(response.data);
    } catch (error) {
      console.error("Error checking email connection:", error);
      setEmailStatus({ connected: false });
    }
  };

  const loadTemplates = async () => {
    try {
      const response = await apiService.getEmailTemplates({
        isActive: true,
        type: "custom",
      });
      setTemplates(response.data.rows || []);
    } catch (error) {
      console.error("Error loading templates:", error);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleTemplateSelect = async (templateId) => {
    if (!templateId) {
      setSelectedTemplate("");
      return;
    }

    setSelectedTemplate(templateId);

    try {
      const response = await apiService.getEmailTemplate(templateId);
      const template = response.data;

      let subject = template.subject;
      let body = template.bodyHtml || template.body;

      // Replace variables with account data
      const primaryContact = accountData?.contacts?.[0];

      const variables = {
        accountName: accountData?.name || "",
        companyName: accountData?.name || "",
        firstName: primaryContact?.firstName || "",
        lastName: primaryContact?.lastName || "",
        fullName: primaryContact
          ? `${primaryContact.firstName} ${primaryContact.lastName}`
          : "",
        email: primaryContact?.email || accountData?.email || "",
        phone: primaryContact?.phone || accountData?.phone || "",
        website: accountData?.website || "",
        industry: accountData?.industry || "",
        city: accountData?.billingCity || "",
        state: accountData?.billingState || "",
        country: accountData?.billingCountry || "",
      };

      Object.keys(variables).forEach((key) => {
        const regex = new RegExp(`{{\\s*${key}\\s*}}`, "g");
        subject = subject.replace(regex, variables[key]);
        body = body.replace(regex, variables[key]);
      });

      setFormData({
        ...formData,
        subject,
        body,
        bodyHtml: body,
      });
    } catch (error) {
      console.error("Error loading template:", error);
      alert("Failed to load template");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.to || !formData.subject || !formData.body) {
      alert("Please fill in all required fields");
      return;
    }

    if (!emailStatus?.connected) {
      alert("Email is not connected. Please connect your email in settings.");
      return;
    }

    setLoading(true);

    try {
      const emailData = {
        to: formData.to,
        subject: formData.subject,
        body: formData.body,
        bodyHtml: formData.bodyHtml || formData.body,
        cc: formData.cc ? formData.cc.split(",").map((e) => e.trim()) : [],
        bcc: formData.bcc ? formData.bcc.split(",").map((e) => e.trim()) : [],
        templateId: selectedTemplate || null,
        trackOpens: formData.trackOpens,
        trackLinks: formData.trackLinks,
      };

      if (selectedTemplate) {
        await apiService.sendAccountTemplateEmail(
          accountData.id,
          selectedTemplate,
          emailData,
        );
      } else {
        await apiService.sendAccountEmail(accountData.id, emailData);
      }

      alert("Email sent successfully!");
      onSuccess();
    } catch (error) {
      console.error("Error sending email:", error);
      alert(
        error.response?.data?.message ||
          "Failed to send email. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div
        className={`border rounded-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col ${
          isDarkMode
            ? "bg-[#0C0E12] border-gray-700"
            : "bg-white border-gray-300"
        }`}
      >
        <div
          className={`flex items-center justify-between p-6 border-b ${
            isDarkMode ? "border-gray-700" : "border-gray-200"
          }`}
        >
          <div>
            <h2
              className={`text-lg font-semibold ${
                isDarkMode ? "text-white" : "text-gray-900"
              }`}
            >
              Send Email
            </h2>
            <p
              className={`text-sm mt-1 ${
                isDarkMode ? "text-gray-400" : "text-gray-500"
              }`}
            >
              {accountData?.name}
            </p>
            {emailStatus && (
              <p
                className={`text-sm mt-1 ${
                  emailStatus.connected
                    ? isDarkMode
                      ? "text-green-400"
                      : "text-green-600"
                    : isDarkMode
                      ? "text-yellow-400"
                      : "text-yellow-600"
                }`}
              >
                {emailStatus.connected
                  ? `Sending from: ${emailStatus.email}`
                  : "Email not connected"}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className={`transition-colors ${
              isDarkMode
                ? "text-gray-400 hover:text-white"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-4">
            {/* Template Selector */}
            <div>
              <label
                className={`block text-sm font-medium mb-2 ${
                  isDarkMode ? "text-gray-400" : "text-gray-600"
                }`}
              >
                <FileText size={16} className="inline mr-1" />
                Use Template
              </label>
              <select
                value={selectedTemplate}
                onChange={(e) => handleTemplateSelect(e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  isDarkMode
                    ? "bg-[#13161B] border-gray-700 text-white focus:border-blue-500"
                    : "bg-white border-gray-300 text-gray-900 focus:border-blue-500"
                }`}
              >
                <option value="">-- Select Template --</option>
                {templates.map((template) => (
                  <option key={template.id} value={template.id}>
                    {template.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label
                className={`block text-sm font-medium mb-2 ${
                  isDarkMode ? "text-gray-400" : "text-gray-600"
                }`}
              >
                To <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                name="to"
                value={formData.to}
                onChange={handleChange}
                required
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  isDarkMode
                    ? "bg-[#13161B] border-gray-700 text-white focus:border-blue-500"
                    : "bg-white border-gray-300 text-gray-900 focus:border-blue-500"
                }`}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  CC
                </label>
                <input
                  type="text"
                  name="cc"
                  value={formData.cc}
                  onChange={handleChange}
                  placeholder="email1@example.com, email2@example.com"
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    isDarkMode
                      ? "bg-[#13161B] border-gray-700 text-white placeholder-gray-500 focus:border-blue-500"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500"
                  }`}
                />
              </div>

              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  BCC
                </label>
                <input
                  type="text"
                  name="bcc"
                  value={formData.bcc}
                  onChange={handleChange}
                  placeholder="email1@example.com, email2@example.com"
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    isDarkMode
                      ? "bg-[#13161B] border-gray-700 text-white placeholder-gray-500 focus:border-blue-500"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500"
                  }`}
                />
              </div>
            </div>

            <div>
              <label
                className={`block text-sm font-medium mb-2 ${
                  isDarkMode ? "text-gray-400" : "text-gray-600"
                }`}
              >
                Subject <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                required
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  isDarkMode
                    ? "bg-[#13161B] border-gray-700 text-white focus:border-blue-500"
                    : "bg-white border-gray-300 text-gray-900 focus:border-blue-500"
                }`}
              />
            </div>

            <div>
              <label
                className={`block text-sm font-medium mb-2 ${
                  isDarkMode ? "text-gray-400" : "text-gray-600"
                }`}
              >
                Message <span className="text-red-500">*</span>
              </label>
              <textarea
                name="body"
                value={formData.body}
                onChange={handleChange}
                required
                rows={12}
                className={`w-full px-3 py-2 border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  isDarkMode
                    ? "bg-[#13161B] border-gray-700 text-white focus:border-blue-500"
                    : "bg-white border-gray-300 text-gray-900 focus:border-blue-500"
                }`}
              />
            </div>

            <div className="flex gap-4">
              <label
                className={`flex items-center gap-2 text-sm cursor-pointer ${
                  isDarkMode ? "text-gray-400" : "text-gray-600"
                }`}
              >
                <input
                  type="checkbox"
                  name="trackOpens"
                  checked={formData.trackOpens}
                  onChange={handleChange}
                  className={`rounded focus:ring-blue-500 ${
                    isDarkMode
                      ? "border-gray-600 text-blue-500"
                      : "border-gray-300 text-blue-600"
                  }`}
                />
                Track Opens
              </label>
              <label
                className={`flex items-center gap-2 text-sm cursor-pointer ${
                  isDarkMode ? "text-gray-400" : "text-gray-600"
                }`}
              >
                <input
                  type="checkbox"
                  name="trackLinks"
                  checked={formData.trackLinks}
                  onChange={handleChange}
                  className={`rounded focus:ring-blue-500 ${
                    isDarkMode
                      ? "border-gray-600 text-blue-500"
                      : "border-gray-300 text-blue-600"
                  }`}
                />
                Track Link Clicks
              </label>
            </div>
          </div>

          <div
            className={`p-6 border-t flex justify-end gap-3 ${
              isDarkMode ? "border-gray-700" : "border-gray-200"
            }`}
          >
            <button
              type="button"
              onClick={onClose}
              className={`px-4 py-2 rounded-lg transition-colors ${
                isDarkMode
                  ? "bg-gray-700 text-gray-300 hover:bg-gray-600"
                  : "bg-gray-200 text-gray-700 hover:bg-gray-300"
              }`}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !emailStatus?.connected}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                isDarkMode
                  ? "bg-blue-600 hover:bg-blue-700 text-white"
                  : "bg-blue-500 hover:bg-blue-600 text-white"
              }`}
            >
              <Send size={16} />
              {loading ? "Sending..." : "Send Email"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SendAccountEmailModal;
